"use client";

import { useEffect, useState } from "react";

import Image from "next/image";

import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronRightCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import mailIcon from "@repo/ui/assets/HomePage/mail.svg";
import { Button } from "@repo/ui/components/ui/button";
import { Input } from "@repo/ui/components/ui/input";
import { usePageContext } from "@repo/ui/contexts/PageContext";
import type { HomePageContent } from "@repo/ui/types/cms";

import type { NormalizedHomePageSections } from "@/features/home/helpers/normalizeHomePageSections";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Please enter a valid email address."),
});

type FormData = z.infer<typeof schema>;

export default function SubscriptionBar() {
  // ⚙️ CMS content
  const { normalizedSections } = usePageContext<HomePageContent, NormalizedHomePageSections>();
  const { subscribeCard } = normalizedSections;

  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
  });
  useEffect(() => {
    if (errors.email) {
      setIsSuccess(false); // hide success message as soon as input goes invalid
    }
  }, [errors.email]);

  function onSubmit() {
    setIsSuccess(true);
    reset();
  }

  return (
    <section
      className="w-full mb-12 responsive-padding responsive-layout"
      data-testid="subscription-bar"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between p-4 text-white bg-brand-blue gap-7 md:flex-row md:rounded-xl"
        noValidate
      >
        <div className="flex items-center flex-1 gap-4">
          <Image src={subscribeCard?.iconUrl || mailIcon} width={56} height={56} alt="Main Icon" />

          <div className="w-full sm:w-1/2 md:w-45">
            {subscribeCard?.title && (
              <div dangerouslySetInnerHTML={{ __html: subscribeCard.title }} />
            )}
            {subscribeCard?.subTitle && (
              <p className="text-xl font-bold">{subscribeCard.subTitle}</p>
            )}
          </div>
        </div>

        <div className="grid w-full grid-cols-1 sm:w-auto">
          <div className="flex w-full gap-1 sm:w-1/2 md:w-full">
            <Input
              type="email"
              placeholder={subscribeCard?.emailPlaceholderText || "Email"}
              {...register("email")}
              aria-invalid={errors.email ? "true" : "false"}
              className={`w-2xl border bg-white text-black ${
                errors.email ? "border-red-600" : "border-gray-300"
              }`}
            />
            <Button
              variant="secondary"
              type="submit"
              className="transition-colors hover:bg-[#00bdd6] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {subscribeCard?.buttonText || "Subscribe"}
              <ChevronRightCircle className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {errors.email && (
            <p role="alert" className="mt-1 text-xs text-red-400">
              {errors.email.message}
            </p>
          )}

          {/* Success message */}
          {isSuccess && (
            <p className="mt-2 text-sm font-semibold text-green-400">Thanks for subscribing!</p>
          )}

          {subscribeCard?.offersInfo && (
            <div
              className="w-full mt-4 text-xs text-white md:mt-2 [&_a]:underline [&_a]:cursor-pointer"
              dangerouslySetInnerHTML={{ __html: subscribeCard.offersInfo }}
            />
          )}
        </div>
      </form>
    </section>
  );
}
